import React, { useEffect, useRef, useState } from 'react';
import { Input } from './ui';
import {
  JOB_TITLE_OPTIONS,
  JOB_TITLE_OTHER,
  resolveJobTitleSelection,
  resolveJobTitleValue,
} from '../utils';

const selectClassName =
  'w-full px-4 py-3.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-h-[48px] text-base md:px-3 md:py-2 md:min-h-0';

interface JobTitleFieldProps {
  value: string;
  onChange: (jobTitle: string) => void;
  required?: boolean;
}

export const JobTitleField: React.FC<JobTitleFieldProps> = ({
  value,
  onChange,
  required = false,
}) => {
  const initial = resolveJobTitleSelection(value);
  const [selection, setSelection] = useState(initial.selection);
  const [customTitle, setCustomTitle] = useState(initial.customTitle);
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const next = resolveJobTitleSelection(value);
    setSelection(next.selection);
    setCustomTitle(next.customTitle);
    lastEmitted.current = value;
  }, [value]);

  const emit = (nextSelection: string, nextCustom: string) => {
    const resolved = resolveJobTitleValue(nextSelection, nextCustom);
    lastEmitted.current = resolved;
    onChange(resolved);
  };

  const isOther = selection === JOB_TITLE_OTHER;

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor="job-title-select" className="block text-sm font-medium text-gray-700 mb-1">
          Job Title
        </label>
        <select
          id="job-title-select"
          value={selection}
          onChange={e => {
            const nextSelection = e.target.value;
            setSelection(nextSelection);
            if (nextSelection === JOB_TITLE_OTHER) {
              emit(JOB_TITLE_OTHER, customTitle);
              return;
            }
            setCustomTitle('');
            emit(nextSelection, '');
          }}
          required={required && !isOther}
          className={selectClassName}
        >
          <option value="">Select job title</option>
          {JOB_TITLE_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {isOther && (
        <Input
          label="Specify job title"
          name="job_title_other"
          value={customTitle}
          onChange={e => {
            const nextCustom = e.target.value;
            setCustomTitle(nextCustom);
            emit(JOB_TITLE_OTHER, nextCustom);
          }}
          placeholder="e.g., Senior Support Worker"
          required={required}
        />
      )}
    </div>
  );
};
